import React, { useState } from 'react';

import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProgramLibraryTableStore } from 'store/programLibraryTable.store';
import { useVersionStore } from 'store/version.store';

import { useMutationDownLoadXerFile } from 'api/mutations/downLoadXerFile.mutation';
import { useMutationRemoveXerFile } from 'api/mutations/removeXerFile.mutation';

import { initialFiltersState } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Checkbox } from 'components/DesignSystem/Checkbox';
import { CustomizedConfirmDeleteModal } from 'components/common';
import { ActionMenuError } from 'components/widgets/RenderLibraryTable/components/ActionMenuError/ActionMenuError';
import { ActionMenuOk } from 'components/widgets/RenderLibraryTable/components/ActionMenuOk/ActionMenuOk';
import { ActionMenuPending } from 'components/widgets/RenderLibraryTable/components/ActionMenuPending/ActionMenuPending';
import { RenameVersionModal } from 'components/widgets/RenderLibraryTable/components/RenameVersionModal/RenameVersionModal';
import classes from 'components/widgets/RenderLibraryTable/components/RowOptionsCell/rowOptionsCell.module.scss';
import { UploadToOpcModal } from 'components/widgets/RenderLibraryTable/components/UploadToOpcModal/UploadToOpcModal';

export const RowOptionsCell = ({
    index,
    status,
    errorMessage,
    id,
    sourceFile,
    versionHashCode,
    isSelected,
    toggleRowSelected,
    versionName,
}) => {
    const { opcEnabled } = useUserHook();
    const isMultiple = useProgramLibraryTableStore((state) => state.isMultiple);
    const [showModal, setShowModal] = useState<'confirmDelete' | 'renameVersion' | 'uploadToOpc' | null>(null);

    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { ability } = useUserHook();
    const isAdmin = ability.can('all', 'Admin');
    const isFileDownload = ability.can('view', 'FileDownload');
    const isCopyVersionAllowed = ability.can('view', 'FileDownload'); // Copy-version has same permission as download permission

    const { mutate: mutateRemoveXerFile, isLoading: isLoadingRemoveXerFile } = useMutationRemoveXerFile();
    const { mutate: mutateDownLoadXerFile, isLoading: isLoadingDownloadXerFile } = useMutationDownLoadXerFile();

    const { sendEventWithDimensions } = useEventWithDimensions();

    const { setVersion } = useVersionStore();
    const updateReplace = useGlobalFilterDataStore((state) => state.updateReplace);
    const handleDeleteFiles = () => {
        mutateRemoveXerFile(
            { ids: [id] },
            {
                onSuccess: () => {
                    handleSnackBar('File was deleted', 'success');
                    setVersion(null);
                    updateReplace(initialFiltersState);

                    sendEventWithDimensions({
                        category: 'Program Library',
                        action: 'Delete Version',
                        label: `version id: ${id}`,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    setShowModal(null);
                    return queryClient.invalidateQueries('getProjectVersions');
                },
            },
        );
    };
    const handleDownloadFile = () => {
        mutateDownLoadXerFile(
            { id: id, fileName: sourceFile },
            {
                onSuccess: () => {
                    handleSnackBar('File downloaded successfully', 'success');

                    sendEventWithDimensions({
                        category: 'Program Library',
                        action: 'Download file',
                        label: sourceFile,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    setShowModal(null);
                    return queryClient.invalidateQueries('getProjectVersions');
                },
            },
        );
    };

    const onUpload = () => {
        setShowModal('uploadToOpc');
    };

    return (
        <>
            {!isMultiple ? (
                <div className={classes.container}>
                    {status.toLowerCase() === 'ok' && (
                        <ActionMenuOk
                            id={id}
                            versionHashCode={versionHashCode}
                            status={status}
                            errorMessage={errorMessage}
                            index={index}
                            setShowModal={() => setShowModal('confirmDelete')}
                            onDownLoad={handleDownloadFile}
                            onUpload={onUpload}
                            isLoading={isLoadingDownloadXerFile}
                            isFileDownload={isFileDownload}
                            isAllowedToCopyVersion={isCopyVersionAllowed}
                            setShowRenameVersionModal={() => setShowModal('renameVersion')}
                        />
                    )}
                    {status.toLowerCase() === 'pending' && <ActionMenuPending className={classes.loaderTheme} />}
                    {status.toLowerCase() === 'error' && (
                        <ActionMenuError
                            index={index}
                            setShowModal={() => setShowModal('confirmDelete')}
                            isFileDownload={isFileDownload}
                            onDownLoad={handleDownloadFile}
                        />
                    )}
                </div>
            ) : (
                <Checkbox
                    checked={isSelected}
                    disabled={!isAdmin && status.toLowerCase() === 'pending'}
                    onChange={() => toggleRowSelected(!isSelected)}
                />
            )}

            <CustomizedConfirmDeleteModal
                isShowModal={showModal === 'confirmDelete'}
                hideModal={() => {
                    setShowModal(null);
                }}
                text={
                    'Are you sure you want to delete this program version? Note all its associated cards will be deleted.'
                }
                onConfirm={handleDeleteFiles}
                isLoading={isLoadingRemoveXerFile}
            />

            <RenameVersionModal
                id={id}
                versionName={versionName}
                showRenameVersionModal={showModal === 'renameVersion'}
                setShowRenameVersionModal={() => setShowModal(null)}
            />
            {opcEnabled && (
                <UploadToOpcModal
                    open={showModal === 'uploadToOpc'}
                    onClose={() => setShowModal(null)}
                    isLoading={false}
                    disabled={false}
                    id={id}
                />
            )}
        </>
    );
};
